import { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { persistAnswer, updateClient, useCoAnswer, useCoClient } from "../../data/store";
import {
  tallyTaxApi,
  taxMapBusy,
  type TallyRegistration,
  type TallyTaxState,
} from "../../data/tallyTaxApi";
import { Callout } from "../../components/Callout";
import { Spinner } from "../../components/ConnectionSequence";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";
import { TallyTaxMapTable } from "./TallyTaxMapTable";
import { INDIAN_STATES } from "../../../../shared/utils/locationUtils";

const ANSWER_KEY = "verifySetup.gstReview";
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const INPUT_CLASS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

type FilingFrequency = "monthly" | "qrmp" | "composition";

interface GstReviewAnswer {
  filingFrequency?: FilingFrequency;
}

type GstReviewDraft = Record<string, GstReviewAnswer>;

interface RegistrationCard extends TallyRegistration {
  key: string;
}

const REGISTRATION_TYPES = ["Regular", "Composition", "SEZ", "Unregistered"];
const STATE_NAMES = INDIAN_STATES.map((item) => item.name);

const FILING_FREQUENCIES: { value: FilingFrequency; label: string; registration: string[] }[] = [
  { value: "monthly", label: "Monthly", registration: ["regular", "sez", "unregistered", ""] },
  { value: "qrmp", label: "QRMP (quarterly)", registration: ["regular", "sez", "unregistered", ""] },
  { value: "composition", label: "Composition (CMP-08, quarterly)", registration: ["composition", ""] },
];

function registrationKey(type: string): string {
  return type.trim().toLowerCase();
}

function frequenciesFor(type: string) {
  const key = registrationKey(type);
  if (key === "") return FILING_FREQUENCIES;
  const matched = FILING_FREQUENCIES.filter((item) => item.registration.includes(key));

  return matched.length > 0 ? matched : FILING_FREQUENCIES.filter((item) => item.value !== "composition");
}

function frequencyKey(card: RegistrationCard): string {
  const gstin = card.gstin.trim().toUpperCase();

  return gstin || `card:${card.key}`;
}

function started(card: RegistrationCard): boolean {
  return (
    card.name.trim() !== ""
    || card.gstin.trim() !== ""
    || card.state.trim() !== ""
    || card.registration_type.trim() !== ""
  );
}

function duplicateMessage(list: RegistrationCard[]): string | null {
  const names = new Set<string>();
  const gstins = new Set<string>();
  for (const card of list) {
    if (!started(card)) continue;
    const gstin = card.gstin.trim().toUpperCase();
    const name = (card.name.trim() || gstin).toLowerCase();
    if (name !== "") {
      if (names.has(name)) {
        return `Two registrations cannot share the name ${card.name.trim() || gstin}.`;
      }
      names.add(name);
    }
    if (GSTIN_RE.test(gstin) && gstins.has(gstin)) {
      return `Two registrations cannot share the GSTIN ${gstin}.`;
    }
    if (GSTIN_RE.test(gstin)) gstins.add(gstin);
  }

  return null;
}

function cardsFrom(tax: TallyTaxState): RegistrationCard[] {
  const rows = tax.registrations ?? [];
  if (rows.length === 0 && tax.gstin.trim() !== "") {
    return [
      {
        key: "legacy",
        id: null,
        name: "",
        gstin: tax.gstin,
        state: tax.gst_state,
        registration_type: tax.gst_registration_type,
        is_primary: true,
        origin: "tally",
      },
    ];
  }

  return rows.map((row, index) => ({
    ...row,
    key: row.id != null ? `id-${row.id}` : `read-${index}-${row.gstin}`,
  }));
}

let localKey = 0;

export function TallyGstStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const draft = useCoAnswer<GstReviewDraft>(clientId, ANSWER_KEY) ?? {};
  const { goNext } = useSubStepNav(clientId, "gst-review");
  const [tax, setTax] = useState<TallyTaxState | null>(null);
  const [cards, setCards] = useState<RegistrationCard[] | null>(null);
  const dirty = useRef(false);
  const waitingForTally = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savingRegistration, setSavingRegistration] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void tallyTaxApi.state(clientId).then(
      (next) => {
        if (!cancelled) setTax(next);
      },
      (reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load GST settings.");
      }
    );

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (dirty.current || tax === null) return;
    setCards(cardsFrom(tax));
    const primary = (tax.registrations ?? []).find((row) => row.is_primary) ?? (tax.registrations ?? [])[0];
    const gstin = (primary?.gstin || tax.gstin || "").trim();
    const stateName = (primary?.state || tax.gst_state || "").trim();
    const registrationType = (primary?.registration_type || tax.gst_registration_type || "").trim();
    if (!client) return;
    if (
      gstin === client.tallyGstin
      && stateName === client.tallyGstState
      && registrationType === client.tallyGstRegistrationType
    ) {
      return;
    }
    updateClient(clientId, {
      tallyGstin: gstin,
      tallyGstState: stateName,
      tallyGstRegistrationType: registrationType,
    });
  }, [client, clientId, tax]);

  const creating = tax ? taxMapBusy(tax.components) : false;
  const writing = tax?.registrations_writing === true;
  const watching = creating || writing || savingRegistration || tax?.gst_refreshing === true;

  useEffect(() => {
    if (!savingRegistration || busy || writing) return;
    setSavingRegistration(false);
  }, [busy, savingRegistration, writing]);

  useEffect(() => {
    if (!watching) return undefined;
    const timer = window.setInterval(() => {
      void tallyTaxApi.state(clientId).then(setTax).catch(() => undefined);
    }, 2000);

    return () => window.clearInterval(timer);
  }, [clientId, watching]);

  useEffect(() => {
    if (!waitingForTally.current || tax === null) return;
    if (tax.registrations_writing || tax.gst_refreshing) return;
    waitingForTally.current = false;
    if (tax.registration_error) return;
    void goNext();
  }, [goNext, tax]);

  const list = cards ?? [];
  const withGstin = list.filter((card) => card.gstin.trim() !== "");
  const registered = list.some((card) => GSTIN_RE.test(card.gstin.trim().toUpperCase()));
  const gstinInvalid = list.some((card) => {
    const gstin = card.gstin.trim().toUpperCase();

    return gstin !== "" && !GSTIN_RE.test(gstin);
  });
  const unfinished = list.some((card) => started(card) && !GSTIN_RE.test(card.gstin.trim().toUpperCase()));
  const filingMissing = list.some((card) => {
    const gstin = card.gstin.trim().toUpperCase();

    return GSTIN_RE.test(gstin) && draft[frequencyKey(card)]?.filingFrequency === undefined;
  });
  const duplicate = duplicateMessage(list);

  function patchCard(key: string, changes: Partial<RegistrationCard>, previousGstin?: string) {
    dirty.current = true;
    setCards((current) =>
      (current ?? []).map((card) => (card.key === key ? { ...card, ...changes } : card))
    );
    if (previousGstin !== undefined && changes.gstin !== undefined) {
      const from = frequencyKey({ key, gstin: previousGstin } as RegistrationCard);
      const to = frequencyKey({ key, gstin: changes.gstin } as RegistrationCard);
      if (from !== to && draft[from]) {
        const next = { ...draft, [to]: draft[from] };
        delete next[from];
        void persistAnswer<GstReviewDraft>(clientId, ANSWER_KEY, next);
      }
    }
  }

  function makePrimary(key: string) {
    dirty.current = true;
    setCards((current) =>
      (current ?? []).map((card) => ({ ...card, is_primary: card.key === key }))
    );
  }

  function removeCard(card: RegistrationCard) {
    dirty.current = true;
    const draftKey = frequencyKey(card);
    if (draft[draftKey]) {
      const next = { ...draft };
      delete next[draftKey];
      void persistAnswer<GstReviewDraft>(clientId, ANSWER_KEY, next);
    }
    setCards((current) => {
      const next = (current ?? []).filter((item) => item.key !== card.key);
      if (next.length > 0 && !next.some((item) => item.is_primary)) {
        next[0] = { ...next[0], is_primary: true };
      }
      return next;
    });
  }

  function addCard() {
    dirty.current = true;
    localKey += 1;
    setCards((current) => [
      ...(current ?? []),
      {
        key: `new-${localKey}`,
        id: null,
        name: "",
        gstin: "",
        state: "",
        registration_type: "",
        is_primary: (current ?? []).length === 0,
        origin: "manual",
      },
    ]);
  }

  async function run(work: () => Promise<TallyTaxState>) {
    setBusy(true);
    setError(null);
    try {
      setTax(await work());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update the tax mapping.");
    } finally {
      setBusy(false);
    }
  }

  function registrationsToSave(source: RegistrationCard[]) {
    return source.filter(started).map((item) => ({
      id: item.id,
      name: item.name,
      gstin: item.gstin,
      state: item.state,
      registration_type: item.registration_type,
      is_primary: item.is_primary,
      origin: item.origin,
    }));
  }

  async function createInTally(card: RegistrationCard) {
    const gstin = card.gstin.trim().toUpperCase();
    if (!GSTIN_RE.test(gstin)) {
      setError("Enter a 15-character GSTIN before creating the registration in Tally.");
      return;
    }
    if (card.state.trim() === "") {
      setError("Choose the state before creating the registration in Tally.");
      return;
    }
    if (duplicate) {
      setError(duplicate);
      return;
    }
    setSavingRegistration(true);
    setBusy(true);
    setError(null);
    try {
      const saved = await tallyTaxApi.saveRegistrations(
        clientId,
        registrationsToSave(list.filter((item) => item.id !== null || item.key === card.key))
      );
      const othersUnsaved = list.some((item) => item.key !== card.key && item.id === null);
      dirty.current = othersUnsaved;
      setTax(saved);
      const savedRows = saved.registrations ?? [];
      setCards((current) =>
        (current ?? []).map((item) => {
          const match = savedRows.find(
            (row) =>
              row.gstin.trim().toUpperCase() === item.gstin.trim().toUpperCase() && item.gstin.trim() !== ""
          );
          if (!match || (item.id === null && item.key !== card.key)) return item;
          return {
            ...item,
            id: match.id,
            origin: match.origin,
            is_primary: match.is_primary,
            name: match.name || item.name,
            state: match.state || item.state,
            registration_type: match.registration_type || item.registration_type,
          };
        })
      );
      const primary = savedRows.find((row) => row.is_primary);
      updateClient(clientId, {
        tallyGstin: primary?.gstin ?? saved.gstin ?? "",
        tallyGstState: primary?.state ?? saved.gst_state ?? "",
        tallyGstRegistrationType: primary?.registration_type ?? saved.gst_registration_type ?? "",
      });
    } catch (reason) {
      setSavingRegistration(false);
      setError(reason instanceof Error ? reason.message : "Could not create the GST registration in Tally.");
    } finally {
      setBusy(false);
    }
  }

  async function continueNext() {
    if (gstinInvalid || unfinished) {
      setError("Enter a 15-character GSTIN on each registration, or leave a blank card empty.");
      return;
    }
    if (duplicate) {
      setError(duplicate);
      return;
    }
    if (filingMissing) {
      setError("Choose how often each registration files.");
      return;
    }
    if (registered && tax?.components_mapped !== true) {
      setError("Map every GST component's input and output ledgers before continuing.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await tallyTaxApi.saveRegistrations(clientId, registrationsToSave(list));
      dirty.current = false;
      setTax(saved);
      const primary = (saved.registrations ?? []).find((row) => row.is_primary);
      updateClient(clientId, {
        tallyGstin: primary?.gstin ?? saved.gstin ?? "",
        tallyGstState: primary?.state ?? saved.gst_state ?? "",
        tallyGstRegistrationType: primary?.registration_type ?? saved.gst_registration_type ?? "",
      });
      if (saved.registrations_writing || saved.gst_refreshing) {
        waitingForTally.current = true;
        return;
      }
      await goNext();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save the GST details.");
    } finally {
      setBusy(false);
    }
  }

  const mapped = !registered || tax?.components_mapped === true;
  const ready =
    tax !== null
    && cards !== null
    && mapped
    && !filingMissing
    && !gstinInvalid
    && !unfinished
    && duplicate === null
    && !busy
    && !savingRegistration
    && tax.registrations_writing !== true
    && tax.gst_refreshing !== true;
  const locked = busy || writing || savingRegistration || tax?.gst_refreshing === true;
  const clientName = client?.name || "this client";

  return (
    <SubStepShell
      title="GST Settings"
      description={
        registered
          ? `GST registrations read from ${clientName}'s Tally company. Each one stays editable. GST components need an input ledger for bills and an output ledger for invoices, both under Duties and Taxes.`
          : `GST registrations read from ${clientName}'s Tally company. Leave them blank when this company is not registered. GST ledgers are not required.`
      }
      thenWhat={
        ready
          ? "Continue → TDS & TCS Settings."
          : registered
            ? "Map every GST component's input and output ledgers, and choose a filing frequency for each registration."
            : "Leave the registrations blank to continue, or enter one and map its input and output ledgers."
      }
      onContinue={() => void continueNext()}
      continueDisabled={!ready}
    >
      {error ? <Callout variant="warning">{error}</Callout> : null}
      {duplicate ? <Callout variant="warning">{duplicate}</Callout> : null}
      {tax?.registration_error ? <Callout variant="warning">{tax.registration_error}</Callout> : null}
      {savingRegistration || tax?.registrations_writing ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          Updating the GST registrations in the open Tally company…
        </p>
      ) : null}
      {tax?.gst_refreshing ? (
        <p className="text-sm text-muted-foreground">Reading the GST registrations from the open Tally company…</p>
      ) : null}
      {cards === null ? (
        <p className="text-sm text-muted-foreground">Reading the GST registrations…</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {list.map((card) => {
            const gstin = card.gstin.trim().toUpperCase();
            const invalid = gstin !== "" && !GSTIN_RE.test(gstin);
            const options = frequenciesFor(card.registration_type);
            const choices =
              card.registration_type !== "" && !REGISTRATION_TYPES.includes(card.registration_type)
                ? [card.registration_type, ...REGISTRATION_TYPES]
                : REGISTRATION_TYPES;
            const frequency = draft[frequencyKey(card)]?.filingFrequency;
            const heading = gstin || card.name.trim() || "New registration";
            const stateOptions =
              card.state !== "" && !STATE_NAMES.includes(card.state) ? [card.state, ...STATE_NAMES] : STATE_NAMES;
            const gstinTaken =
              gstin !== ""
              && list.some((other) => other.key !== card.key && other.gstin.trim().toUpperCase() === gstin);
            const canCreate =
              card.id === null
              && card.origin === "manual"
              && GSTIN_RE.test(gstin)
              && card.state.trim() !== ""
              && !gstinTaken;
            const unsent = card.id === null && card.origin === "manual";

            return (
              <li key={card.key} className="flex flex-col gap-3 rounded-lg border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono font-medium">{heading}</span>
                  <div className="flex items-center gap-2">
                    {card.is_primary ? (
                      <span className="text-xs text-muted-foreground">primary</span>
                    ) : withGstin.length > 1 && card.gstin.trim() !== "" ? (
                      <button
                        type="button"
                        onClick={() => makePrimary(card.key)}
                        disabled={locked}
                        className="rounded-md border px-2 py-1 text-xs font-medium text-foreground"
                      >
                        Make primary
                      </button>
                    ) : null}
                    {unsent ? (
                      <button
                        type="button"
                        aria-label="Remove registration"
                        onClick={() => removeCard(card)}
                        disabled={locked}
                        className="flex size-7 items-center justify-center rounded-md border text-sm leading-none text-muted-foreground"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                    Name
                    <input
                      className={INPUT_CLASS}
                      value={card.name}
                      maxLength={255}
                      disabled={locked}
                      onChange={(event) => patchCard(card.key, { name: event.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                    GSTIN
                    <input
                      className={INPUT_CLASS}
                      value={card.gstin}
                      maxLength={15}
                      disabled={locked}
                      onChange={(event) =>
                        patchCard(card.key, { gstin: event.target.value.toUpperCase() }, card.gstin)
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                    State
                    <select
                      className={INPUT_CLASS}
                      value={card.state}
                      disabled={locked}
                      onChange={(event) => patchCard(card.key, { state: event.target.value })}
                    >
                      <option value="">Choose</option>
                      {stateOptions.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                    Registration type
                    <select
                      className={INPUT_CLASS}
                      value={card.registration_type}
                      disabled={locked}
                      onChange={(event) => {
                        const next = event.target.value;
                        patchCard(card.key, { registration_type: next });
                        const stillValid = frequenciesFor(next).some((item) => item.value === frequency);
                        if (frequency && !stillValid) {
                          void persistAnswer<GstReviewDraft>(clientId, ANSWER_KEY, {
                            ...draft,
                            [frequencyKey(card)]: {},
                          });
                        }
                      }}
                    >
                      <option value="">Not reported</option>
                      {choices.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                  {GSTIN_RE.test(gstin) ? (
                    <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                      Filing frequency
                      <select
                        className={INPUT_CLASS}
                        value={frequency ?? ""}
                        disabled={locked}
                        onChange={(event) => {
                          const value = event.target.value as FilingFrequency | "";
                          void persistAnswer<GstReviewDraft>(clientId, ANSWER_KEY, {
                            ...draft,
                            [frequencyKey(card)]: value === "" ? {} : { filingFrequency: value },
                          });
                        }}
                      >
                        <option value="">Choose</option>
                        {options.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
                {invalid ? (
                  <p className="text-xs text-destructive">Enter a 15-character GSTIN, or leave it blank.</p>
                ) : null}
                {gstinTaken ? (
                  <p className="text-xs text-destructive">This GSTIN is already on another registration.</p>
                ) : null}
                {unsent && GSTIN_RE.test(gstin) ? (
                  <button
                    type="button"
                    onClick={() => void createInTally(card)}
                    disabled={locked || !canCreate}
                    className="w-fit rounded-lg border px-4 py-2 text-[13px] font-medium text-foreground disabled:opacity-50"
                  >
                    Create in Tally
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={addCard}
        disabled={locked}
        className="w-fit rounded-lg border px-4 py-2 text-[13px] font-medium text-foreground"
      >
        Add registration
      </button>

      {registered ? (
        tax === null ? (
          <p className="text-sm text-muted-foreground">Reading the GST components…</p>
        ) : (
          <TallyTaxMapTable
            rows={tax.components}
            dutyLedgers={tax.duty_ledgers}
            busy={locked}
            createLocked={creating || writing}
            onMap={(row, name, side) => void run(() => tallyTaxApi.mapLedger(clientId, row.kind, row.id, name, side))}
            onCreate={(row, name, side) => void run(() => tallyTaxApi.createLedger(clientId, row.kind, row.id, name, side))}
          />
        )
      ) : null}
    </SubStepShell>
  );
}
